'use client';

import { useState } from 'react';
import Image from 'next/image';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface GalleryImage {
  src: string; // Supabase Storage path
  alt: string;
  caption?: string;
}

interface GalleryProps {
  images: GalleryImage[];
  columns?: 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  aspectRatio?: 'square' | '16/9' | '4/3' | '3/2';
}

function getImageUrl(src: string): string {
  const bucket = src.startsWith('photo-gallery/') ? 'photo-gallery' : 'blog-images';
  const storagePath = bucket === 'photo-gallery'
    ? src.replace('photo-gallery/', '')
    : src;
  const baseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  return `${baseUrl}/storage/v1/object/public/${bucket}/${storagePath}`;
}

export function Gallery({
  images,
  columns = 3,
  gap = 'md',
  aspectRatio
}: GalleryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
  };

  const columnClasses = {
    2: 'grid-cols-2 sm:grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
  };

  const aspectRatioClass = aspectRatio ? {
    'square': 'aspect-square',
    '16/9': 'aspect-video',
    '4/3': 'aspect-[4/3]',
    '3/2': 'aspect-[3/2]',
  }[aspectRatio] : null;

  // Prepare images for lightbox
  const lightboxSlides = images.map((img) => ({
    src: getImageUrl(img.src),
    alt: img.alt,
    title: img.caption,
  }));

  return (
    <>
      <div className={`grid ${columnClasses[columns]} ${gapClasses[gap]}`}>
        {images.map((img, index) => {
          const thumbnailUrl = getImageUrl(img.src);

          return (
            <div
              key={index}
              className="relative cursor-pointer group overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 transition-all hover:shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex(index);
                setIsOpen(true);
              }}
            >
              {aspectRatioClass ? (
                <div className={`relative ${aspectRatioClass}`}>
                  <Image
                    src={thumbnailUrl}
                    alt={img.alt}
                    fill
                    className="object-cover transition-transform group-hover:scale-105"
                    sizes={`(max-width: 768px) 50vw, ${100 / columns}vw`}
                  />
                </div>
              ) : (
                <Image
                  src={thumbnailUrl}
                  alt={img.alt}
                  width={600}
                  height={400}
                  className="w-full h-auto transition-transform group-hover:scale-105"
                  sizes={`(max-width: 768px) 50vw, ${100 / columns}vw`}
                />
              )}
              {img.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                  {img.caption}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        index={photoIndex}
        slides={lightboxSlides}
        carousel={{ finite: images.length <= 1 }}
      />
    </>
  );
}
